/**
 * Pure helpers for the Lab's Services views. Service calls are recorded per
 * patient-message ORDINAL (1 = the run's first patient message), not by the
 * runtime's own turn number, so a turn is mapped by its position in the run.
 */
import type {
  PreviewRunServices,
  PreviewServiceCall,
} from "@/api/previewInsights";

/** 1-based position of `turnNumber` among the run's recorded turns; null when absent. */
export function turnOrdinal(
  turns: readonly { turnNumber: number }[],
  turnNumber: number,
): number | null {
  const index = turns.findIndex((turn) => turn.turnNumber === turnNumber);
  return index < 0 ? null : index + 1;
}

/** The calls recorded for the Nth patient message of the run (empty when none). */
export function callsForOrdinal(
  services: PreviewRunServices | undefined,
  ordinal: number | null,
): readonly PreviewServiceCall[] {
  if (services === undefined || ordinal === null) {
    return [];
  }
  return services.turns.find((turn) => turn.turn === ordinal)?.calls ?? [];
}

export type FeatureSource = NonNullable<
  PreviewRunServices["featureSources"]
>[string];

export type FeatureEntry = {
  name: string;
  value: boolean | string;
  /** Which layer decided the value; absent when the target did not say. */
  source?: FeatureSource;
};

/** The run's pack (brain) feature map as a list; empty when none was recorded. */
export function featureEntries(
  services: PreviewRunServices | undefined,
): FeatureEntry[] {
  return Object.entries(services?.features ?? {}).map(([name, value]) => {
    const source = services?.featureSources?.[name];
    return source === undefined ? { name, value } : { name, value, source };
  });
}

/** The run's service flags (liv-api features) as a list; empty when none was recorded. */
export function serviceFlagEntries(
  services: PreviewRunServices | undefined,
): FeatureEntry[] {
  return Object.entries(services?.serviceFlags ?? {}).map(([name, flag]) => ({
    name,
    value: flag.value,
    source: flag.source,
  }));
}

export const FEATURE_SOURCE_LABELS = {
  pack: "pack",
  run_option: "run option",
  tenant: "tenant",
  default: "default",
  forced_off: "forced off",
} as const satisfies Record<FeatureSource, string>;

export function featureValueLabel(value: boolean | string): string {
  return typeof value === "boolean" ? (value ? "on" : "off") : value;
}

/** "name: value (source)" for one feature or service flag. */
export function featureEntryLabel(entry: FeatureEntry): string {
  const base = `${entry.name}: ${featureValueLabel(entry.value)}`;
  return entry.source === undefined
    ? base
    : `${base} (${FEATURE_SOURCE_LABELS[entry.source]})`;
}

/**
 * Run options (ADR 0016): the service features a clinician can choose per run.
 * liv-api owns the real registry and refuses an unknown name or value; this
 * list only drives the form.
 */
export const RUN_OPTIONS = [
  {
    name: "safety_preview",
    label: "Safety check",
    values: [
      { value: "off", label: "Off", help: "Safety is not run for this run." },
      {
        value: "shadow",
        label: "Shadow",
        help: "Safety runs and is recorded, but never changes Liv's reply.",
      },
      {
        value: "on",
        label: "On",
        help: "Safety runs and can change Liv's reply, as for a real patient.",
      },
    ],
  },
  {
    name: "prompt_injection_v2",
    label: "Prompt injection check (v2)",
    values: [
      {
        value: "off",
        label: "Off",
        help: "Messages are not checked for prompt injection.",
      },
      {
        value: "shadow",
        label: "Shadow",
        help: "Each patient message is checked and recorded, but never changes Liv's reply.",
      },
      {
        value: "on",
        label: "On",
        help: "A message judged an injection is not sent to Liv; a fixed safe reply is shown instead.",
      },
    ],
  },
] as const;

export type LabFlags = Record<string, string>;

/** The form's starting choice: every option explicitly Off, so a run never inherits tenant config. */
export function defaultLabFlags(): LabFlags {
  return Object.fromEntries(
    RUN_OPTIONS.map((o) => [o.name, o.values[0].value]),
  );
}

export function resultJson(result: PreviewServiceCall["result"]): string {
  return JSON.stringify(result, null, 2);
}

/** The safety service's verdict for one patient message, as liv-api records it (`outcome` is the concern level). */
export function safetyCallOf(
  calls: readonly PreviewServiceCall[],
): PreviewServiceCall | null {
  return calls.find((call) => call.service === "safety") ?? null;
}

export function concernTone(
  outcome: string,
): "outline" | "warning" | "destructive" {
  if (outcome === "none" || outcome === "skipped_policy") return "outline";
  if (outcome === "mild") return "warning";
  return "destructive";
}

export function safetyCategories(call: PreviewServiceCall): string[] {
  const value = call.result["categories"];
  return Array.isArray(value) ? value.map(String) : [];
}
